import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { Button } from '@mui/material'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import { useUIMode } from '../../../context/UIContext'
import { useScanTarget } from '../../../context/ScanTargetContext'
import { playScanBeep } from '../../../utils/scanBeep'
import { CameraScanButtonProps } from './types'
import { DEFAULT_SCAN_REGION } from './scanRegion'
import { CAMERA_SCAN_MESSAGES, isCameraSupported } from './messages'
import { cameraScanButtonSx } from './CameraScan.styles'
import { CameraScanDialog } from './CameraScanDialog'

/**
 * Boton reutilizable que abre la camara, lee un codigo de barras y devuelve el
 * texto por `onScan`. Pensado para acompanar a los inputs de escaneo del WMS,
 * donde el operario sin lector fisico necesita una alternativa.
 *
 * El chunk con ZXing (~250 KB) se descarga recien al primer tap, no en la carga
 * de la pagina: este archivo no importa nada de @zxing/*, el import dinamico
 * vive en CameraScanDialog.
 *
 * En mobile el boton ademas es el "destino" del lector fisico (ver
 * ScanTargetContext): lo que se escanea sin ningun campo enfocado llega por el
 * mismo `onScan`, en los dos modos de captura. Por eso se monta siempre, aunque
 * en modo handheld no se vea.
 */
export const CameraScanButton: React.FC<CameraScanButtonProps> = ({
  onScan,
  label = 'Escanear con Cámara',
  dialogTitle = 'Escanear código',
  helperText = 'Centra el código dentro del recuadro',
  formats,
  timeoutMs = 10000,
  torchDelayMs = 4000,
  restrictToFrame = true,
  scanRegion = DEFAULT_SCAN_REGION,
  debug = false,
  disabled = false,
  hidden = false,
  inputRef,
  fullWidth = true,
  sx,
  onCancel,
  onError
}) => {
  const [open, setOpen] = useState(false)
  // Preferencia por dispositivo (perfil > MODO DE CAPTURA). Ya incluye el gate de
  // mobile, asi que las pantallas que usan el boton no repiten ese chequeo.
  const { isCameraCaptureEnabled, isScanBeepEnabled } = useUIMode()

  // Destino del lector fisico en mobile, aunque el boton no se muestre.
  const anchorRef = useScanTarget({ onScan, disabled, enabled: !hidden, inputRef })

  const handleOpen = () => {
    // El chequeo de soporte se hace antes de montar el dialogo para no descargar
    // el chunk de ZXing en un dispositivo donde la camara no va a funcionar.
    if (!isCameraSupported()) {
      onError?.(CAMERA_SCAN_MESSAGES.insecureContext)
      return
    }
    setOpen(true)
  }

  const handleClose = useCallback(() => {
    setOpen(false)
    onCancel?.()
  }, [onCancel])

  const handleDecoded = useCallback(
    (value: string) => {
      setOpen(false)
      // La lectura de la camara equivale al Enter del lector sin foco: mismo beep.
      if (isScanBeepEnabled) playScanBeep()
      onScan(value)
    },
    [onScan, isScanBeepEnabled]
  )

  /**
   * Hace que el boton "atras" del celular cierre el dialogo en vez de navegar.
   *
   * El dialogo no es una entrada del historial, asi que sin esto el atras
   * retrocede la navegacion del SPA y saca al operario de la pantalla, dejando
   * la vista en blanco. El boton fisico no se puede bloquear, pero si se puede
   * empujar una entrada propia al abrir y consumirla al cerrar.
   */
  useEffect(() => {
    if (!open) return

    window.history.pushState({ cameraScanner: true }, '')

    const handlePopState = () => handleClose()
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      /**
       * Si el dialogo se cerro por la X o por una lectura exitosa, la entrada
       * que empujamos sigue en el historial y hay que sacarla, o el proximo
       * atras no haria nada visible.
       *
       * Si se cerro POR el atras, el navegador ya la consumio: el state deja de
       * tener la marca y no hay que retroceder de nuevo —hacerlo sacaria al
       * operario de la pantalla, que es justo lo que se quiere evitar—.
       */
      if (window.history.state?.cameraScanner) window.history.back()
    }
  }, [open, handleClose])

  /**
   * Si el modo de captura deja de estar disponible con el dialogo abierto, hay
   * que cerrarlo explicitamente en vez de dejar que el `return null` lo desmonte
   * de golpe. El caso real es rotar una tablet: cruza el breakpoint de 900px e
   * isMobile pasa a false.
   *
   * Sin esto, MUI intenta devolver el foco al boton que abrio el dialogo —que ya
   * no existe— y el foco cae en <body>: el operario dispara con el lector fisico
   * y no entra nada. Cerrarlo dispara onCancel, que reenfoca el input.
   */
  useEffect(() => {
    if (open && !isCameraCaptureEnabled) handleClose()
  }, [open, isCameraCaptureEnabled, handleClose])

  // Los hooks van todos arriba de este return: no mover.
  if (hidden) return null
  // En handheld no hay boton, pero el ancla tiene que estar en el DOM: con ella
  // se sabe si el destino vive dentro del modal abierto.
  if (!isCameraCaptureEnabled) return <span ref={anchorRef} hidden />

  return (
    <Fragment>
      <span ref={anchorRef} hidden />
      <Button
        fullWidth={fullWidth}
        variant='outlined'
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<PhotoCameraOutlinedIcon />}
        sx={[cameraScanButtonSx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {label}
      </Button>

      <CameraScanDialog
        open={open}
        title={dialogTitle}
        helperText={helperText}
        formats={formats}
        timeoutMs={timeoutMs}
        torchDelayMs={torchDelayMs}
        restrictToFrame={restrictToFrame}
        scanRegion={scanRegion}
        debug={debug}
        onClose={handleClose}
        onDecoded={handleDecoded}
        onError={onError}
      />
    </Fragment>
  )
}
